import React from 'react';
import Yup from '~/util/yup';
import {
	addPatientPhysicianOptions,
	editPatientPhysicianOptions,
	getInDraftOrderForUserOptions,
	getPatientPhysiciansOptions,
	PatientPhysicianAssociationEnum,
	PatientPhysicianAssociationOptions,
	VerificationStatusEnum
} from '@generated';
import { AutocompleteSearchFormField } from '~/shared/forms/controls/fields/AutocompleteSearchFormField';
import { TextAreaFormField } from '~/shared/forms/controls/fields/text-fields/TextAreaFormField';
import { CheckboxFormField } from '~/shared/forms/controls/fields/CheckboxFormField';
import { useQuery } from '@tanstack/react-query';
import { FormModal } from '~/shared/forms/FormModal';
import { SelectFormField } from '~/shared/forms/controls/fields/SelectFormField';
import type { PatientPhysicianModel } from '../types';
import { useIdentity } from '~/features/authentication/identity/IdentityContext';

function getFilteredOptions(existingPhysician?: PatientPhysicianModel, currentPrimaryPhysician?: PatientPhysicianModel) {
	let options = PatientPhysicianAssociationOptions;
	if (currentPrimaryPhysician && existingPhysician?.id !== currentPrimaryPhysician.id) {
		options = options.filter(o => o.id !== PatientPhysicianAssociationEnum.Primary);
	}
	if (existingPhysician?.patientPhysicianAssociationId !== PatientPhysicianAssociationEnum.Referring) {
		options = options.filter(o => o.id !== PatientPhysicianAssociationEnum.Referring);
	}
	return options;
}

export function PatientPhysicianFormModal({
	stayId,
	existingPhysician,
	quickAddPhysicianId,
	closeModal
}: {
	stayId: string;
	existingPhysician?: PatientPhysicianModel;
	quickAddPhysicianId?: string;
	closeModal: () => void;
}) {
	const { loggedInUser } = useIdentity();
	const { data: order, isLoading: orderLoading } = useQuery(getInDraftOrderForUserOptions(stayId, loggedInUser.id));
	const { data: patientPhysicians, isLoading } = useQuery(getPatientPhysiciansOptions(stayId));

	if (isLoading || !patientPhysicians) {
		return null;
	}

	const currentPrimaryPhysician = patientPhysicians.physicians.find(
		p => p.patientPhysicianAssociationId === PatientPhysicianAssociationEnum.Primary
	);
	const existingPhysicianIds = patientPhysicians.physicians
		.filter(x => x.patientPhysicianAssociationId !== PatientPhysicianAssociationEnum.Referring)
		.map(p => p.physicianId);

	const filtredAssociationTypeOptions = getFilteredOptions(existingPhysician, currentPrimaryPhysician);

	const validationSchema = Yup.object({
		physicianId: !existingPhysician
			? Yup.string().label('Physician').notOneOf(existingPhysicianIds, 'Physician already exists').nullable().required()
			: Yup.string().label('Physician').nullable().required(),
		patientPhysicianAssociationId: Yup.string().nullable().label('Association Type'),
		notes: Yup.string().label('Notes').nullable(),
		verifiedWithPatient: Yup.bool().label('Verified with Patient')
	});

	const initialValues = existingPhysician
		? {
				physicianId: existingPhysician.physicianId,
				patientPhysicianId: existingPhysician.id,
				patientPhysicianAssociationId: existingPhysician.patientPhysicianAssociationId,
				notes: existingPhysician.notes,
				verifiedWithPatient: false
		  }
		: {
				physicianId: quickAddPhysicianId ?? null,
				patientPhysicianAssociationId: null,
				notes: null,
				verifiedWithPatient: false
		  };

	const showVerification =
		patientPhysicians.verificationState.verificationStatusId === VerificationStatusEnum.Verified ||
		patientPhysicians.verificationState.verificationStatusId === VerificationStatusEnum.HasUnverifiedChanges;
	return (
		<FormModal
			title={`${existingPhysician ? 'Edit' : 'Add'} Patient Physician`}
			initialValues={{
				stayId,
				...initialValues
			}}
			saveMutation={{
				...(existingPhysician ? editPatientPhysicianOptions : addPatientPhysicianOptions),
				invalidateQueryKeys: [getPatientPhysiciansOptions(stayId).queryKey]
			}}
			validationSchema={validationSchema}
			onSuccess={closeModal}
			closeModal={closeModal}
		>
			{formHelpers => {
				const associationType = formHelpers.values.patientPhysicianAssociationId
					? Number(formHelpers.values.patientPhysicianAssociationId)
					: null;
				const acceptingOrders = formHelpers.values.acceptingOrders as boolean;
				return (
					<div className="grid grid-cols-1 gap-4">
						<AutocompleteSearchFormField
							name="physicianId"
							searchType="physician"
							isDisabled={Boolean(existingPhysician) || Boolean(quickAddPhysicianId)}
						/>
						<SelectFormField
							name="patientPhysicianAssociationId"
							label="Association Type"
							isReadOnly={
								existingPhysician?.patientPhysicianAssociationId === PatientPhysicianAssociationEnum.Primary ||
								existingPhysician?.patientPhysicianAssociationId === PatientPhysicianAssociationEnum.Referring
							}
							options={filtredAssociationTypeOptions}
						/>
						{showVerification && !existingPhysician && <CheckboxFormField name="verifiedWithPatient" hideLabel />}
						{order &&
							!existingPhysician &&
							associationType &&
							associationType !== PatientPhysicianAssociationEnum.Primary &&
							acceptingOrders && <CheckboxFormField name="addToOrder" hideLabel />}
						<TextAreaFormField name="notes" />
					</div>
				);
			}}
		</FormModal>
	);
}
